import { filterClasses } from "@swivl-mono/helpers";
import { LocationVarInheritance } from "@swivl-mono/types";
import { ReactNode } from "react";
import { TbExternalLink } from "react-icons/tb";

import { RowInheritanceControls } from "./RowInheritanceControls";

interface CortexRowShellProps {
  label: string;
  description?: string;
  /** If set, renders next to the label (e.g. a "Saved" indicator). */
  titleStatus?: ReactNode;
  /** If set, renders a link icon next to the label that opens this URL in a new tab. */
  helpUrl?: string;
  inheritance: LocationVarInheritance;
  /** True when the operator has started editing an override but not saved yet. */
  overrideStaged: boolean;
  /** Human-readable value a revert would land on, shown on the Revert tooltip. */
  revertsTo?: string;
  /** False on topics gated by an Edit button, where Customize has nothing to add. */
  showCustomize?: boolean;
  onCustomize: () => void;
  onRevert: () => void;
  /** Discards a staged-but-unsaved override draft, returning the field to its inherited state. */
  onDiscard: () => void;
  children: ReactNode;
  /** If set, renders this under the children (e.g. inline error text). */
  footer?: ReactNode;
  /**
   * Replaces the default inheritance pill + Customize/Revert cluster. Used by
   * pages on the Details v3 edit model, where a section-level Edit button takes
   * the place of the per-row Customize gate.
   */
  controls?: ReactNode;
  /**
   * "horizontal" lays the row out on Details v3's Facility Info grid — label,
   * control, then the pill/Revert cluster — with the description on a second grid
   * row under the control. Defaults to the stacked layout so existing callers are
   * unaffected.
   */
  layout?: "stacked" | "horizontal";
  /**
   * This row is revealed by a choice on the row above, so its label is indented to
   * read as belonging to it. Horizontal layout only.
   */
  nested?: boolean;
  /**
   * This row reveals gated rows directly beneath it, so it drops its bottom padding
   * and the group's wrapper closes it instead — keeping parent and child 8px apart.
   */
  hasRevealed?: boolean;
}

export function CortexRowShell({
  label,
  description,
  titleStatus,
  helpUrl,
  inheritance,
  overrideStaged,
  revertsTo,
  showCustomize,
  onCustomize,
  onRevert,
  onDiscard,
  children,
  footer,
  controls,
  layout = "stacked",
  nested = false,
  hasRevealed = false
}: CortexRowShellProps): React.ReactElement {
  // Inside a reveal group the rows sit 8px apart and the wrapper supplies the
  // bottom padding, so only a standalone row carries the full py-4.
  const verticalPadding = nested ? "pt-2" : hasRevealed ? "pt-4" : "py-4";
  const titleCluster = (
    <div className="flex flex-wrap items-center gap-2">
      {label && <label className="body-sm-alt text-grey-900">{label}</label>}
      {helpUrl && (
        <a href={helpUrl} target="_blank" rel="noopener noreferrer" className="text-grey-400 hover:text-orange-500">
          <TbExternalLink className="size-3.5" />
        </a>
      )}
      {titleStatus}
    </div>
  );

  const controlCluster = (
    <div className="flex shrink-0 items-center gap-2">
      {controls ?? (
        <RowInheritanceControls
          inheritance={inheritance}
          overrideStaged={overrideStaged}
          revertsTo={revertsTo}
          showCustomize={showCustomize}
          onCustomize={onCustomize}
          onRevert={onRevert}
          onDiscard={onDiscard}
        />
      )}
    </div>
  );

  if (layout === "horizontal") {
    // The label column is a fixed width, not a range: each row is its own grid, so a
    // flexible label track resolves per row and every control starts at a different x.
    // Fixed keeps them left-aligned down the card, and long labels wrap inside it.
    // The control keeps the design system's 240px form-field width (libs/ui
    // FormField.Helpers). With room to spare the pill/Revert cluster shares the line
    // as a third `max-content` column; in a tight card the label narrows to 200px and
    // the cluster moves onto its own line under the control, left-aligned with it.
    // The switch is a container query on the row's own width (`@container` wrapper +
    // `@max-[780px]`), not a viewport breakpoint: how much room a row has depends on
    // what sits beside its card (the Policies page adds a Generated Text column at
    // 1400px), so a viewport breakpoint was clipping rows in the 1441–1470px band
    // (ENG-2924 QA). 780px is what the three-column line needs: the 280px label, the
    // 240px control, two 16px gaps, and the cluster at its widest ("Customized" pill
    // plus a labelled "Revert to Parent" button, ~205px), with a little slack.
    return (
      <div className="@container">
        <div
          className={filterClasses(
            "grid grid-cols-[280px_minmax(240px,1fr)_max-content] gap-x-4 @max-[780px]:grid-cols-[200px_minmax(240px,1fr)]",
            !nested && "items-center",
            verticalPadding
          )}
        >
          {nested ? (
            // A nested row keeps the label column empty and stacks label, control and
            // description down the control column, so the child block lines up with the
            // control it belongs to. Explicit row placement puts the pill/Revert cluster
            // on the SAME grid row as the control, so it centres on the control rather
            // than on the whole stacked block.
            <>
              <div className="col-start-2 row-start-1">{titleCluster}</div>
              <div className="col-start-2 row-start-2 mt-1 min-w-0">{children}</div>
              <div className="col-start-3 row-start-2 self-center @max-[780px]:col-start-2 @max-[780px]:row-start-3 @max-[780px]:mt-2 @max-[780px]:justify-self-start">
                {controlCluster}
              </div>
              {(description || footer) && (
                <div className="col-start-2 row-start-3 min-w-0 @max-[780px]:row-start-4">
                  {description && <p className="body-sm mt-1 text-grey-600">{description}</p>}
                  {footer}
                </div>
              )}
            </>
          ) : (
            <>
              {titleCluster}
              {/* The control gets a column of its own so the description and footer below
                can share it and match its width exactly, rather than running under the
                pill. */}
              <div className="min-w-0">{children}</div>
              <div className="@max-[780px]:col-start-2 @max-[780px]:row-start-2 @max-[780px]:mt-2 @max-[780px]:justify-self-start">
                {controlCluster}
              </div>
              {description && <p className="body-sm col-start-2 mt-1 text-grey-600">{description}</p>}
              {footer && <div className="col-start-2">{footer}</div>}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="py-4">
      <div className="mb-1 flex items-start justify-between gap-4">
        {titleCluster}
        {controlCluster}
      </div>
      {description && <p className="body-sm mb-2 text-grey-600">{description}</p>}
      {children}
      {footer}
    </div>
  );
}
